/**
 * Spatial grid helpers for InteractiveConstellation's O(n) dot-to-dot link pass.
 *
 * Instead of the O(n²) all-pairs loop, dots are bucketed into cells of size
 * `linkDistance`. For any given dot we only check the 3×3 neighbourhood of
 * its cell (9 cells max), reducing comparisons from n*(n-1)/2 to roughly
 * n * (k / 9) where k is the average number of dots per cell.
 */

export type GridDot = { x: number; y: number }

/** Cell key for a position (x, y) given cell size s. */
export const gridKey = (x: number, y: number, s: number): number => {
  // Pack (col, row) into a single integer. Columns for typical screens and
  // linkDistances stay well under 100k, so col*100000+row stays in safe int range.
  const col = Math.floor(x / s)
  const row = Math.floor(y / s)
  return col * 100_000 + row
}

/** Build a cell → dots map from the current dot array. O(n). */
export const buildGrid = <T extends GridDot>(dots: readonly T[], cellSize: number): Map<number, T[]> => {
  const grid = new Map<number, T[]>()
  for (const d of dots) {
    const k = gridKey(d.x, d.y, cellSize)
    let cell = grid.get(k)
    if (!cell) { cell = []; grid.set(k, cell) }
    cell.push(d)
  }
  return grid
}

/**
 * Return all dots in the 3×3 neighbourhood of (x, y) — every dot that could
 * possibly be within `cellSize` px of the given position. O(neighbours) — at
 * most 9 cells.
 */
export const neighbourDots = <T extends GridDot>(
  x: number,
  y: number,
  grid: Map<number, T[]>,
  cellSize: number,
): T[] => {
  const col = Math.floor(x / cellSize)
  const row = Math.floor(y / cellSize)
  const result: T[] = []
  for (let dc = -1; dc <= 1; dc++) {
    for (let dr = -1; dr <= 1; dr++) {
      const cell = grid.get((col + dc) * 100_000 + (row + dr))
      if (cell) for (const d of cell) result.push(d)
    }
  }
  return result
}
